import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import useFetchIndustries from '@/features/agent/api/useFetchIndustries';
import useFetchCompaniesCustomFields from '../api/useFetchCompaniesCustomFields';
import usePostCompany from '../api/usePostCompany';
import CompaniesCreateForm from '../components/CompaniesCreateForm';
import { createCompaniesCreateSchema } from '../schemas/companiesCreateSchema';
import type { CompaniesCreateInput } from '../schemas/companiesCreateSchema';
import { Button } from '@/components/Button';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import { buildCustomDataPayload } from '@/utils/customDataForm';
import { areRequiredCustomFieldsFilled } from '@/utils/customFieldValidation';

export default function CompaniesNewTemplate() {
  const navigate = useNavigate();
  const { data: industries } = useFetchIndustries();
  const { data: customFields, isLoading: isCustomFieldsLoading } = useFetchCompaniesCustomFields();
  // 必須カスタムフィールドの検証を含めるため、取得した customFields からスキーマを動的生成する
  const schema = useMemo(
    () => createCompaniesCreateSchema(customFields?.records ?? []),
    [customFields?.records]
  );
  const methods = useForm<CompaniesCreateInput>({
    resolver: zodResolver(schema),
    // submit 前に不備が分かるよう、フォーカスアウト時に検証し、以降は入力のたびに再検証する
    mode: 'onBlur',
    reValidateMode: 'onChange',
    // customData を空オブジェクトで初期化しておく。
    // RHF は customData.<数値フィールドID> を配列インデックスとして解釈するため、
    // 初期値が無いと customData が配列として構築され z.record 検証に失敗する。
    // 事前に空オブジェクトを seed することで record（オブジェクト）構造を固定する。
    defaultValues: { customData: {} },
  });
  const { mutateAsync: postCompany, isPending } = usePostCompany();

  // 必須項目（企業名・住所＋必須カスタムフィールド）が埋まるまで新規作成ボタンを無効化する
  const name = methods.watch('name');
  const address = methods.watch('company_information.address');
  const customDataValues = methods.watch('customData');
  // customFields 取得前は schema が空生成され必須カスタムフィールド検証がすり抜けるため、
  // 取得完了までは送信不可にする
  const requiredFilled =
    !isCustomFieldsLoading &&
    !!name?.trim() &&
    !!address?.trim() &&
    areRequiredCustomFieldsFilled(customFields?.records, customDataValues ?? {});

  // クライアント側 zod 検証に失敗した場合のフィードバック。
  // onInvalid を渡さないと検証失敗時に無反応になり、失敗原因が分からない(#2835)。
  const handleInvalid = () => {
    showErrorToast('未記入の必須項目があります。');
    // RHF の再レンダリングでエラー文言が DOM に反映されてからスクロールする
    requestAnimationFrame(() => {
      const firstError = document.querySelector('[role="alert"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleSubmit = async (values: CompaniesCreateInput) => {
    // customFields 取得前は必須カスタムフィールド検証が成立しないため、送信を中断する
    if (isCustomFieldsLoading) return;
    try {
      // customData は zod スキーマに定義済みのため、zodResolver のパース後も values に残る。
      // （スキーマ未定義キーは zod にストリップされる点に注意 — #2790/#2798 の再発防止）
      const { customData, ...rest } = values;
      const payload = {
        ...rest,
        custom_data: buildCustomDataPayload(customData, 'create'),
      };

      const created = await postCompany(payload);
      navigate({
        to: '/agent/companies/$companiesId',
        params: { companiesId: String(created.id) },
      });
      showSuccessToast('企業を作成しました');
    } catch (error) {
      console.error('Error creating company:', error);
      showErrorToast('保存できませんでした。');
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="relative p-6">
        <CompaniesCreateForm
          industries={industries ?? []}
          customFields={customFields?.records ?? []}
          onSubmit={methods.handleSubmit(handleSubmit, handleInvalid)}
          loading={isPending}
        />
      </div>
      <div className="fixed right-0 bottom-0 z-[60] flex w-full justify-end bg-white px-6 py-[14px] shadow-[0_-3px_5px_-3px_#00000040]">
        <Button
          color="primary"
          size="lg"
          onClick={() => {
            methods.handleSubmit(handleSubmit, handleInvalid)();
          }}
          disabled={isPending || !requiredFilled}
          className="z-50"
        >
          新規作成
        </Button>
      </div>
    </FormProvider>
  );
}
